import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Renders an identifier (MRN, Identity/National ID, etc.) with a one-click
 * copy-to-clipboard button. Use anywhere such a number is displayed.
 *
 * <CopyableId value={patient.mrn} />
 * <CopyableId value={patient.identity_number} label="National Number · " kind="Identity number" />
 */
export default function CopyableId({ value, label = "", className = "", iconClassName = "", kind = "Number", testId }) {
  const [copied, setCopied] = useState(false);
  if (value === null || value === undefined || value === "") {
    return <span className={className}>—</span>;
  }
  const text = String(value);

  async function copy(e) {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(`${kind} copied`);
    setTimeout(() => setCopied(false), 1200);
  }

  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid={testId}>
      {label}{text}
      <button
        type="button"
        onClick={copy}
        title={`Copy ${kind.toLowerCase()}`}
        aria-label={`Copy ${kind.toLowerCase()}`}
        className={`opacity-50 hover:opacity-100 hover:text-[#0B7E8C] transition-all shrink-0 ${iconClassName}`}
        data-testid={testId ? `${testId}-copy` : undefined}
      >
        {copied ? <Check className="w-3 h-3 text-[#15803D]" /> : <Copy className="w-3 h-3" />}
      </button>
    </span>
  );
}
